import React, { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { learningService } from '../../../../services/learning'
import {
  coachingService,
  type CoachAskResult,
  type DailyBrief,
  type LearningPlan,
  type WeeklyReview,
  type WorkforceRecommendation,
} from '../../../../services/coaching'
import type { LearningHome } from '../../../../types/learning'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobileErrorState,
  MobileLoadingState,
  MobilePrimaryButton,
  MobileSectionTitle,
} from './WorkerMobilePrimitives'

/**
 * Learning Portal and AI Coach on mobile web.
 *
 * This is the SAME learner-owned platform domain the Android app uses: every
 * value comes from `/api/learning/*` and `/api/learning/coach/*`. No learning
 * state, streak, XP, or competency progression is computed or stored here, and
 * no tenant-owned copy of learning is created. AI provider credentials stay
 * entirely server-side; the browser only sees the coach's reply.
 */

type LearningSegment = 'learn' | 'coach'

export const WorkerMobileLearning: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useI18n()
  const [segment, setSegment] = useState<LearningSegment>('learn')
  const [home, setHome] = useState<LearningHome | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setHome((await learningService.getHome()).data.home)
    } catch (err) {
      setError((err as any)?.response?.data?.message || t('Learning could not be loaded right now.'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  return (
    <div className="wm-screen wm-screen--portal" data-testid="worker-mobile-learning">
      <div className="wm-portal-bar">
        <button type="button" className="wm-icon-button" onClick={onClose} aria-label={t('Back')}>‹</button>
        <h1>{t('Learning')}</h1>
      </div>

      <div className="wm-segmented" role="tablist" aria-label={t('Learning')}>
        <button type="button" role="tab" aria-selected={segment === 'learn'} className="wm-segment" data-active={segment === 'learn' || undefined} onClick={() => setSegment('learn')}>
          {t('Learn')}
        </button>
        <button type="button" role="tab" aria-selected={segment === 'coach'} className="wm-segment" data-active={segment === 'coach' || undefined} onClick={() => setSegment('coach')}>
          {t('Coach')}
        </button>
      </div>

      {segment === 'learn' ? (
        loading ? <MobileLoadingState label={t('Loading your learning…')} />
          : error ? <MobileErrorState message={error} onRetry={() => void load()} />
            : <LearnerHome home={home} />
      ) : <CoachPanel />}
    </div>
  )
}

const LearnerHome: React.FC<{ home: LearningHome | null }> = ({ home }) => {
  const { t } = useI18n()
  if (!home) return <MobileEmptyState title={t('Learning is not set up yet')} detail={t('Start your first lesson to build your learning profile.')} />

  return (
    <>
      <MobileCard tone="info" testId="worker-mobile-learning-streak">
        <div className="wm-card__heading">
          <div>
            <h2>{home.profile?.displayName || t('Your learning')}</h2>
            <p>{t('{days} day streak', { days: String(home.streak?.currentStreakDays || 0) })}</p>
          </div>
          <MobileChip tone="positive">{t('{xp} XP', { xp: String((home.profile as any)?.totalXp ?? (home.profile as any)?.xp ?? 0) })}</MobileChip>
        </div>
        {home.needsOnboarding ? (
          <p className="wm-guidance">{t('Finish your learning setup to get personalized recommendations.')}</p>
        ) : null}
      </MobileCard>

      {home.orgAssignments?.length ? (
        <MobileCard testId="worker-mobile-learning-assignments">
          <MobileSectionTitle title={t('Assigned to you')} />
          {home.orgAssignments.map((assignment: any, index: number) => (
            <div className="wm-inline-card" key={assignment.id || `assignment-${index}`}>
              <strong>{assignment.title || assignment.templateTitle || t('Assigned learning')}</strong>
              {assignment.dueAt ? <small>{t('Due {date}', { date: String(assignment.dueAt).slice(0, 10) })}</small> : null}
            </div>
          ))}
        </MobileCard>
      ) : null}

      {home.continueLearning?.length ? (
        <MobileCard testId="worker-mobile-learning-continue">
          <MobileSectionTitle title={t('Continue learning')} />
          {home.continueLearning.map((skill: any, index: number) => (
            <div className="wm-inline-card" key={skill.skillId || `skill-${index}`}>
              <div className="wm-card__heading">
                <div>
                  <strong>{skill.skillName || t('Skill')}</strong>
                  <small>{t('Level {level}', { level: String(skill.level ?? skill.currentLevel ?? 1) })}</small>
                </div>
                <MobileChip tone="info">{`${Math.round(Number(skill.progressPercent ?? skill.progress ?? 0))}%`}</MobileChip>
              </div>
            </div>
          ))}
        </MobileCard>
      ) : null}

      {home.recommendations?.length ? (
        <MobileCard testId="worker-mobile-learning-recommendations">
          <MobileSectionTitle title={t('Recommended next')} detail={t('Chosen by the platform from your progress and work.')} />
          {home.recommendations.slice(0, 5).map((recommendation: any, index: number) => (
            <div className="wm-inline-card" key={recommendation.id || `recommendation-${index}`}>
              <strong>{recommendation.title || recommendation.skillName || t('Recommended lesson')}</strong>
              {recommendation.reason ? <small>{recommendation.reason}</small> : null}
            </div>
          ))}
        </MobileCard>
      ) : null}

      {home.upcomingCoaching?.length ? (
        <MobileCard testId="worker-mobile-learning-coaching">
          <MobileSectionTitle title={t('Upcoming coaching')} />
          {home.upcomingCoaching.map((session: any, index: number) => (
            <div className="wm-inline-card" key={session.id || `session-${index}`}>
              <strong>{session.title || t('Coaching session')}</strong>
              {session.startsAt ? <small>{String(session.startsAt).slice(0, 16).replace('T', ' ')}</small> : null}
            </div>
          ))}
        </MobileCard>
      ) : null}
    </>
  )
}

const CoachPanel: React.FC = () => {
  const { t } = useI18n()
  const [brief, setBrief] = useState<DailyBrief | null>(null)
  const [plans, setPlans] = useState<LearningPlan[]>([])
  const [review, setReview] = useState<WeeklyReview | null>(null)
  const [workforce, setWorkforce] = useState<WorkforceRecommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [answer, setAnswer] = useState<CoachAskResult | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const settled = await Promise.allSettled([
        coachingService.getBrief(),
        coachingService.getPlan(),
        coachingService.getWeeklyReview(),
        coachingService.getWorkforceRecommendations(),
      ])
      if (cancelled) return
      if (settled[0].status === 'fulfilled') setBrief(settled[0].value.data.brief)
      if (settled[1].status === 'fulfilled') setPlans(settled[1].value.data.plan.plans || [])
      if (settled[2].status === 'fulfilled') setReview(settled[2].value.data.review)
      if (settled[3].status === 'fulfilled') setWorkforce(settled[3].value.data.recommendations || [])
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  const ask = async () => {
    if (!question.trim()) return
    setAsking(true)
    setError('')
    try {
      setAnswer((await coachingService.ask(question.trim())).data)
      setQuestion('')
    } catch (err) {
      setError((err as any)?.response?.data?.message || t('The coach could not answer right now.'))
    } finally {
      setAsking(false)
    }
  }

  if (loading) return <MobileLoadingState label={t('Loading your coach…')} />

  return (
    <>
      {brief ? (
        <MobileCard tone="info" testId="worker-mobile-coach-brief">
          <MobileSectionTitle title={t('Today’s brief')} />
          <p className="wm-lede">{brief.greeting}</p>
          <p>{brief.message}</p>
          {brief.todaysFocus ? (
            <div className="wm-inline-card">
              <strong>{brief.todaysFocus.skill}</strong>
              <small>{t('{minutes} min · {difficulty}', {
                minutes: String(brief.todaysFocus.estimatedMinutes),
                difficulty: brief.todaysFocus.difficulty,
              })}</small>
            </div>
          ) : null}
          {brief.highlights?.length ? (
            <ul className="wm-list">{brief.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}</ul>
          ) : null}
        </MobileCard>
      ) : null}

      {plans.length ? (
        <MobileCard testId="worker-mobile-coach-plan">
          <MobileSectionTitle title={t('Your learning plan')} />
          {plans.map((plan, index) => (
            <div className="wm-inline-card" key={`${plan.skillName}-${index}`}>
              <div className="wm-card__heading">
                <div>
                  <strong>{plan.skillName}</strong>
                  {plan.certificationTitle ? <small>{plan.certificationTitle}</small> : null}
                </div>
                <MobileChip tone="info">{`${Math.round(plan.certificationProgress)}%`}</MobileChip>
              </div>
              {plan.weeks?.slice(0, 3).map((week) => (
                <p className="wm-guidance" key={`week-${week.week}`}>
                  {t('Week {week}', { week: String(week.week) })}: {(week as any).focus || (week as any).summary || ''}
                </p>
              ))}
            </div>
          ))}
        </MobileCard>
      ) : null}

      <MobileCard testId="worker-mobile-coach-ask">
        <MobileSectionTitle title={t('Ask your coach')} />
        <form className="wm-form" onSubmit={(event) => { event.preventDefault(); void ask() }}>
          <label className="wm-field">
            <span>{t('Your question')}</span>
            <textarea rows={2} value={question} onChange={(event) => setQuestion(event.target.value)} />
          </label>
          <MobilePrimaryButton label={t('Ask')} onClick={() => void ask()} busy={asking} disabled={!question.trim()} testId="worker-mobile-coach-ask-submit" />
        </form>
        {error ? <p className="wm-inline-error" role="alert">{error}</p> : null}
        {answer ? (
          <div className="wm-inline-card" data-testid="worker-mobile-coach-answer">
            <p>{answer.reply}</p>
            {answer.suggestedActions?.length ? (
              <ul className="wm-list">{answer.suggestedActions.map((action) => <li key={action}>{action}</li>)}</ul>
            ) : null}
          </div>
        ) : null}
      </MobileCard>

      {workforce.length ? (
        <MobileCard testId="worker-mobile-coach-workforce">
          <MobileSectionTitle title={t('Because of your work')} />
          {workforce.map((recommendation, index) => (
            <div className="wm-inline-card" key={`workforce-${index}`}>
              <strong>{recommendation.suggestedAction}</strong>
              <small>{recommendation.reason}</small>
              {recommendation.relatedJob ? <small>{recommendation.relatedJob}</small> : null}
            </div>
          ))}
        </MobileCard>
      ) : null}

      {review ? (
        <MobileCard testId="worker-mobile-coach-review">
          <MobileSectionTitle title={t('Weekly review')} />
          <p>{review.message}</p>
          <div className="wm-chip-row">
            <MobileChip tone="info">{review.timeStudiedLabel}</MobileChip>
            {review.newCertifications?.map((certification) => <MobileChip tone="positive" key={certification}>{certification}</MobileChip>)}
          </div>
        </MobileCard>
      ) : null}
    </>
  )
}
